import * as React from 'react'

import { MobXProviderContext } from 'mobx-react'

import { IUserProject } from '~/client/graph'
import ConfirmDialogTypes from '~/client/src/desktop/enums/ConfirmDialogTypes'
import DesktopInitialState from '~/client/src/desktop/stores/DesktopInitialState'
import AddEditMemberDialog from '~/client/src/desktop/views/ProjectSetUp/components/ProjectMembersUpload/ProjectMembers/components/AddEditMemberDialog'
import BulkEditMembersSideBar from '~/client/src/desktop/views/ProjectSetUp/components/ProjectMembersUpload/ProjectMembers/components/BulkEditMembersSideBar/BulkEditMembersSideBar'
import ConfirmUsersDialog from '~/client/src/desktop/views/ProjectSetUp/components/ProjectMembersUpload/ProjectMembers/components/ConfirmUsersDialog/ConfirmUsersDialog'
import ProcoreDirectoryDialog from '~/client/src/desktop/views/ProjectSetUp/components/ProjectMembersUpload/ProjectMembers/components/ProcoreDirectoryDialog/ProcoreDirectoryDialog'
import AlertDialog from '~/client/src/shared/components/AlertDialog/AlertDialog'
import ConfirmDialog from '~/client/src/shared/components/ConfirmDialog/ConfirmDialog'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import User from '~/client/src/shared/models/User'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'
import * as e from '~/client/src/shared/stores/EventStore/eventConstants'
import { IServiceUserDto } from '~/client/src/shared/types/UserDto'
import {
  extractPhoneClaimConflict,
  formatBulkSaveError,
  IPhoneClaimConflict,
} from '~/client/src/shared/utils/bulkSaveErrorFormatter'
import canSendInviteToUser from '~/client/src/shared/utils/canSendInviteToUser'

import { ModalState } from '../hooks/useModalState'
import { UserActionsResult } from '../hooks/useUserActions'
import { getPhoneClearTargets } from './phoneClearTargets'

interface IModalsProps {
  modalState: ModalState
  actions: UserActionsResult
  selectedUsers: User[]
  hasInvitableSelected: boolean
  clearSelection: () => void
  onManagePhone: (userId: string) => void
  phoneClearToken: number
}

interface ProvidedStores {
  projectMembersStore: ProjectMembersStore
  userProjectsStore: UserProjectsStore
  state: DesktopInitialState
}

export default function Modals({
  modalState,
  actions,
  selectedUsers,
  hasInvitableSelected,
  clearSelection,
  onManagePhone,
  phoneClearToken,
}: IModalsProps): React.ReactElement {
  const { projectMembersStore, userProjectsStore, state } = React.useContext(
    MobXProviderContext,
  ) as unknown as ProvidedStores

  const isAddOpen = modalState.primaryModal === 'addUser'

  // Surfaced when a save fails on an already-claimed phone, so an admin can
  // transfer the number to the user being saved (STX1-637).
  const [phoneClaimConflict, setPhoneClaimConflict] =
    React.useState<IPhoneClaimConflict | null>(null)
  const resetErrorAndPhoneConflict = React.useCallback(() => {
    modalState.resetErrorMessage()
    setPhoneClaimConflict(null)
  }, [modalState])

  // STX1-637: clearing a member's phone in the edit dialog removes it
  // everywhere. When the removal would strand the source's sign-in, auth asks
  // for confirmation — queue those here and surface one strand prompt at a time.
  const [strandPrompts, setStrandPrompts] = React.useState<
    Array<{ userId: string; message: string }>
  >([])
  const currentStrand = strandPrompts[0] ?? null
  const dequeueStrand = React.useCallback(() => {
    setStrandPrompts(prev => prev.slice(1))
  }, [])
  const confirmStrand = React.useCallback(async () => {
    if (!currentStrand) return
    const result = await projectMembersStore.removeMemberPhone(
      currentStrand.userId,
      true,
    )
    if (result.outcome === 'error') {
      modalState.openAlert(result.message)
    }
    dequeueStrand()
  }, [currentStrand, projectMembersStore, modalState, dequeueStrand])

  const itemsToEdit: IServiceUserDto[] = React.useMemo(() => {
    if (modalState.context.newProjectMember) {
      return [modalState.context.newProjectMember]
    }
    return modalState.context.itemsToEdit
  }, [modalState.context.newProjectMember, modalState.context.itemsToEdit])

  const handleSubmitAddEdit = React.useCallback(
    async (
      items: User[],
      _lastUpdatedItemId?: string,
      reassignClaimedPhone?: boolean,
    ) => {
      // Diff against the pristine store entry BEFORE the save (saveMembers
      // preserves a blank, so an emptied number needs an explicit removal).
      const clearTargets = getPhoneClearTargets(
        items,
        id => projectMembersStore.getById(id)?.phoneNumber,
      )
      try {
        await actions.saveMembers(
          items as unknown as IServiceUserDto[],
          reassignClaimedPhone,
        )
        setPhoneClaimConflict(null)
        modalState.closePrimary()
      } catch (err: any) {
        modalState.setError(formatBulkSaveError(err))
        setPhoneClaimConflict(extractPhoneClaimConflict(err))
        // Save failed → never trigger a removal.
        return
      }

      // Save succeeded → apply the full removals sequentially.
      for (const id of clearTargets) {
        const result = await projectMembersStore.removeMemberPhone(id)
        if (result.outcome === 'confirm-strand') {
          setStrandPrompts(prev => [
            ...prev,
            { userId: id, message: result.message },
          ])
        } else if (result.outcome === 'error') {
          modalState.openAlert(result.message)
        }
      }
    },
    [actions, modalState, projectMembersStore],
  )

  const handleInviteFromEdit = React.useCallback(
    async (user: User, cb: () => void) => {
      try {
        const ok = await actions.inviteMembers([user])
        if (!ok) {
          modalState.openAlert(
            Localization.translator.somethingWentWrongDuringAPIInteraction,
          )
        }
      } catch (err: any) {
        modalState.setError(err?.message || '')
      } finally {
        cb()
      }
    },
    [actions, modalState],
  )

  const handleConfirmInvite = React.useCallback(
    async (users: User[]) => {
      try {
        const ok = await actions.inviteMembers(users)
        if (!ok) {
          modalState.closeConfirm()
          modalState.openAlert(
            Localization.translator.somethingWentWrongDuringAPIInteraction,
          )
          return
        }
        clearSelection()
        modalState.close()
      } catch (err: any) {
        modalState.closeConfirm()
        modalState.openAlert(err?.message || '')
      }
    },
    [actions, modalState, clearSelection],
  )

  const handleConfirmApprove = React.useCallback(
    async (users: User[]) => {
      try {
        await actions.approveMembers(users)
        clearSelection()
        modalState.close()
      } catch (err: any) {
        modalState.closeConfirm()
        modalState.openAlert(err?.message || '')
      }
    },
    [actions, modalState, clearSelection],
  )

  const handleConfirmSetNotInvited = React.useCallback(
    async (users: User[]) => {
      try {
        await actions.setMembersAsNotInvited(users)
        clearSelection()
        modalState.close()
      } catch (err: any) {
        modalState.closeConfirm()
        modalState.openAlert(err?.message || '')
      }
    },
    [actions, modalState, clearSelection],
  )

  const handleBulkEditSave = React.useCallback(
    async (items: IUserProject[]) => {
      try {
        await actions.updateMembersSettings(items)
        clearSelection()
        modalState.closePrimary()
      } catch (err: any) {
        modalState.setError(err?.message || '')
      }
    },
    [actions, modalState, clearSelection],
  )

  const handleConfirmDelete = React.useCallback(
    async (users: User[]) => {
      try {
        await actions.deleteMembers(users)
        clearSelection()
        modalState.close()
      } catch (err: any) {
        modalState.closeConfirm()
        modalState.openAlert(err?.message || '')
      }
    },
    [actions, modalState, clearSelection],
  )

  const handleAddFromProcore = React.useCallback(
    async (users: User[]) => {
      const dtos = users.map(u => u.asJson as IServiceUserDto)
      try {
        await actions.saveMembers(dtos)
        modalState.closePrimary()
      } catch (err: any) {
        modalState.openAlert(
          formatBulkSaveError(
            err,
            '\n',
            users.map(u => u.fullName),
          ),
        )
      }
    },
    [actions, modalState],
  )

  const isSaving = !!state.loading.get(e.SAVE_MEMBERS)
  const isInviting = !!state.loading.get(e.INVITE_PROJECT_MEMBERS)
  const isApproving = !!state.loading.get(e.APPROVE_PROJECT_MEMBERS)
  const isSettingNotInvited = !!state.loading.get(
    e.SET_PROJECT_MEMBERS_AS_NOT_INVITED,
  )
  const isUpdatingSettings = !!state.loading.get(e.SAVE_USER_PROJECTS)

  return (
    <>
      <AddEditMemberDialog
        isOpen={isAddOpen}
        closeDialog={modalState.closePrimary}
        loading={isSaving}
        onSubmit={handleSubmitAddEdit}
        onInvite={handleInviteFromEdit}
        onManagePhone={onManagePhone}
        phoneClearToken={phoneClearToken}
        itemsToEdit={itemsToEdit as unknown as User[]}
        allItems={projectMembersStore.list}
        errorMessage={modalState.context.errorMessage}
        resetErrorMessage={resetErrorAndPhoneConflict}
        phoneClaimConflict={phoneClaimConflict}
      />

      <ConfirmUsersDialog
        type={ConfirmDialogTypes.INVITE}
        isOpen={modalState.confirmModal === 'invite'}
        loading={isInviting}
        closeDialog={modalState.closeConfirm}
        performItems={handleConfirmInvite}
        itemsToPerform={modalState.context.pendingInviteUsers}
      />

      <ConfirmUsersDialog
        type={ConfirmDialogTypes.APPROVE}
        isOpen={modalState.confirmModal === 'approve'}
        loading={isApproving}
        closeDialog={modalState.closeConfirm}
        performItems={handleConfirmApprove}
        itemsToPerform={modalState.context.pendingApproveUsers}
      />

      <ConfirmUsersDialog
        type={ConfirmDialogTypes.SET_NOT_INVITED}
        isOpen={modalState.confirmModal === 'setNotInvited'}
        loading={isSettingNotInvited}
        closeDialog={modalState.closeConfirm}
        performItems={handleConfirmSetNotInvited}
        itemsToPerform={modalState.context.pendingSetNotInvitedUsers}
      />

      {modalState.primaryModal === 'bulkEdit' && (
        <BulkEditMembersSideBar
          onClose={modalState.closePrimary}
          selectedProjectMembers={selectedUsers}
          bulkEditTags={handleBulkEditSave}
          loading={isUpdatingSettings}
          errorMessage={modalState.context.errorMessage}
          toggleInviteDialog={isShow => {
            if (isShow) {
              modalState.openInvite(
                selectedUsers.filter(user =>
                  canSendInviteToUser(user, userProjectsStore),
                ),
              )
            }
          }}
          toggleDeleteDialog={isShow => {
            if (isShow) {
              modalState.openDelete(selectedUsers)
            }
          }}
          hasProjectMembersToInvite={hasInvitableSelected}
        />
      )}

      <ConfirmUsersDialog
        type={ConfirmDialogTypes.DELETE}
        isOpen={modalState.confirmModal === 'delete'}
        loading={isUpdatingSettings}
        closeDialog={modalState.closeConfirm}
        performItems={handleConfirmDelete}
        itemsToPerform={modalState.context.pendingDeleteUsers}
      />

      <ProcoreDirectoryDialog
        isOpen={modalState.primaryModal === 'procore'}
        procoreCompanies={modalState.context.procoreCompanies}
        addToProject={handleAddFromProcore}
        closeDialog={modalState.closePrimary}
        openAlert={modalState.openAlert}
        loading={isSaving}
      />

      <AlertDialog
        title={Localization.translator.error}
        isOpen={modalState.isAlertOpen}
        onClose={modalState.closeAlert}
      >
        <span className="tw:text-destructive tw:whitespace-pre-line">
          {modalState.context.errorMessage}
        </span>
      </AlertDialog>

      <ConfirmDialog
        isOpen={!!currentStrand}
        title="Remove phone number?"
        question={currentStrand?.message}
        doneTitle="Remove anyway"
        useRedConfirmationButton
        onDoneClicked={confirmStrand}
        onCancelClicked={dequeueStrand}
      />
    </>
  )
}
